// The public numbers of a civilization that are worth reporting when they change, in display order
const StatLabels = {
    culture: "culture",
    cultureProduction: "culture/turn",
    science: "science",
    scienceProduction: "science/turn",
    strength: "strength",
    food: "food",
    foodProduction: "food/turn",
    resources: "resources",
    resourceProduction: "resources/turn",
    happyFaces: "happy faces",
    civilActions: "civil actions",
    militaryActions: "military actions",
    population: "population",
    // Tokens owned (gained or lost for good, e.g. Catherine the Great's decree; not those moving between bank and cards)
    yellowTokens: "yellow tokens",
    blueTokens: "blue tokens",
    militaryCards: "military cards",
}

const GetStats = (player) => ({
    culture: player.culture,
    cultureProduction: player.GetCultureProduction(),
    science: player.science,
    scienceProduction: player.GetScienceProduction(),
    strength: player.GetMilitaryStrength(),
    food: player.GetFood(),
    // (less what the workers eat)
    foodProduction: player.GetNetFoodProduction(),
    resources: player.GetResources(),
    resourceProduction: player.GetResourceProduction(),
    happyFaces: player.GetHappyFaces(),
    // The actions the civilization has each turn (a government change, a wonder...); Hammurabi's military action used as
    // a civil action is not one more, so it is left out (it comes and goes as military actions are spent)
    civilActions: player.SumDelegate(player.civilActionCalculationDelegate.filter(d => !d.MilitaryAsCivil)),
    militaryActions: player.GetMilitaryActionCount(),
    // Workers in the pool and on cards
    population: player.workerPool + player.technologies.reduce((sum, tech) => sum + tech.GetWorkerCount(), 0),
    yellowTokens: player.GetYellowTokens(),
    blueTokens: player.GetBlueTokens(),
    militaryCards: player.militaryHand.length,
})

// Every player's stats by name
const GetAllStats = (gameState) =>
    Object.fromEntries(gameState.playerStates.map(player => [player.name, GetStats(player)]));

// What changed between two sets of stats: [{ stat, label, change }] for the non-zero changes only
const StatsDelta = (before, after) => Object.keys(StatLabels)
    .map(stat => ({ stat, label: StatLabels[stat], change: (after?.[stat] ?? 0) - (before?.[stat] ?? 0) }))
    .filter(entry => entry.change !== 0);

// Left out of turn summaries: totals that change every turn through production (the production is reported
// instead), and routine changes other players do not need to hear about (cards drawn, population growth)
const NotInTurnSummaries = ["culture", "science", "food", "resources", "militaryCards", "population"];

// Per player, what changed between two GetAllStats results; players with no changes are left out.
// forTurnSummary leaves out the stats in NotInTurnSummaries (an event's own changes keep everything).
const PlayerChanges = (beforeAll, afterAll, forTurnSummary = false) => Object.keys(afterAll)
    .map(player => ({
        player,
        changes: StatsDelta(beforeAll[player], afterAll[player]).filter(entry => !forTurnSummary || !NotInTurnSummaries.includes(entry.stat)),
    }))
    .filter(entry => entry.changes.length > 0);

// Combines players whose changes are exactly the same, e.g. everyone drawing 3 military cards, or the players who
// made the same choice for an event: [{ players: [names], allPlayers, changes }], in the order players first appear.
// allPlayers is true when the group is every player in the game (allPlayerNames).
const GroupChanges = (entries, allPlayerNames) => {
    const groups = [];
    entries.forEach(entry => {
        const key = entry.changes.map(change => `${change.stat}:${change.change}`).sort().join("|");
        const group = groups.find(g => g.key === key);
        if (group)
        {
            group.players.push(entry.player);
        } else {
            groups.push({ key, players: [entry.player], changes: entry.changes });
        }
    });
    return groups.map(({ players, changes }) => ({
        players,
        allPlayers: allPlayerNames.length > 1 && allPlayerNames.every(name => players.includes(name)),
        changes,
    }));
};

module.exports = {
    GroupChanges,
    StatLabels,
    GetStats,
    GetAllStats,
    StatsDelta,
    PlayerChanges,
}